import React, { useEffect, useState } from "react";
import { Table, Tag, notification, Grid, Button, Select } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import {
  Output,
  WalletTransactionInterface,
} from "../../../services/api/transaction/types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  transactionActions,
  transactionSelectors,
} from "../../../store/transaction";
import "./style.css";
import { CopyOutlined } from "@ant-design/icons";
import haveToApi from "../../../services/api/transaction/api";
import { userWalletSelectors } from "../../../store/wallet";

const { useBreakpoint } = Grid;

const WalletTransactions: React.FC = () => {
  const screens = useBreakpoint();
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [cursors, setCursors] = useState<string[]>([new Date().toISOString()]);
  const [transactionCount, setTransactionCount] = useState<number>(10);
  const walletAddress = useAppSelector(
    userWalletSelectors.getUserWalletAddress,
  );
  const explorerURL = process.env.REACT_APP_EXPLORER_URL;

  const copyKey = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const walletTransaction = useAppSelector(
    transactionSelectors.getWalletTransactions,
  );
  const loader = useAppSelector(transactionSelectors.getLoader);
  const dispatch = useAppDispatch();

  const getTransactionCount = () => {
    haveToApi
      .getWalletTransactionCount({ Address: walletAddress })
      .then((result) => {
        setTransactionCount(result.count);
      })
      .catch((error) => {
        notification.error({ message: error?.detail.split(":")[1] });
      });
  };

  useEffect(() => {
    if (walletAddress) {
      getTransactionCount();
      setCurrentPage(1);
      setCursors([new Date().toISOString()]);
      dispatch(
        transactionActions.getWalletTransactions({
          Address: walletAddress,
          Limit: 5,
          Before: new Date().toISOString(),
        }),
      );
    }
  }, [dispatch, pageSize, walletAddress]);

  useEffect(() => {
    if (walletTransaction.length > 0 && currentPage === cursors.length - 1) {
      const lastCreatedAt =
        walletTransaction[walletTransaction.length - 1].created_at;
      setCursors((prev) => [...prev, lastCreatedAt]);
    }
  }, [walletTransaction, currentPage, cursors.length]);

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      dispatch(
        transactionActions.getWalletTransactions({
          Address: walletAddress,
          Limit: pageSize,
          Before: cursors[currentPage - 2],
        }),
      );
    }
  };

  const handleNext = () => {
    if (currentPage * pageSize < transactionCount) {
      const lastCreatedAt =
        walletTransaction[walletTransaction.length - 1].created_at;
      setCursors((prev) => [...prev, lastCreatedAt]);
      setCurrentPage(currentPage + 1);
      dispatch(
        transactionActions.getWalletTransactions({
          Address: walletAddress,
          Limit: pageSize,
          Before: lastCreatedAt,
        }),
      );
    }
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setCurrentPage(1);
    setCursors([new Date().toISOString()]);
    dispatch(
      transactionActions.getWalletTransactions({
        Address: walletAddress,
        Limit: value,
        Before: new Date().toISOString(),
      }),
    );
  };

  const columns: ColumnsType<WalletTransactionInterface> = [
    {
      key: "hash_key",
      title: "Hash",
      dataIndex: "hash_key",
      className: "max-width-250",
      ellipsis: true,
      render: (hash_key: string) => (
        <div className="text-primary-color">
          <CopyOutlined
            onClick={() => {
              copyKey(hash_key);
            }}
          />
          <a
            href={`${explorerURL}/explorer/transaction/${hash_key}`}
            target="_blank"
          >
            {`${hash_key}`}
          </a>
        </div>
      ),
      sorter: (a, b) => a.hash_key.localeCompare(b.hash_key),
    },
    {
      key: "block_id",
      title: "Block",
      className: "max-width-250",
      dataIndex: "block_id",
      render: (block_id: string) => (
        <div className="grey">{block_id ? block_id : "-"}</div>
      ),
    },
    {
      key: "inputs",
      title: "Inputs",
      dataIndex: "inputs",
      render: (inputs) => <div className="grey">{inputs?.length}</div>,
    },
    {
      key: "outputs",
      title: "Outputs",
      dataIndex: "outputs",
      render: (outputs) => {
        return <div className="grey">{outputs?.length}</div>;
      },
    },
    {
      key: "transaction_type_",
      title: "Type",
      dataIndex: "transaction_type",
      render: (transaction_type) => {
        return <div className="grey"> {`${transaction_type}`}</div>;
      },
      sorter: (a, b) => a.transaction_type!.localeCompare(b.transaction_type!),
    },
    {
      key: "transaction_status",
      title: "Status",
      dataIndex: "transaction_status",
      render: (transaction_status: string | null) => {
        return <div className="grey"> {`${transaction_status}`}</div>;
      },
      sorter: (a, b) =>
        a.transaction_status!.localeCompare(b.transaction_status!),
    },
    {
      key: "created_at",
      title: "Date-time",
      dataIndex: "created_at",
      render: (created_at: string) => (
        <div className="grey">
          {created_at.endsWith("Z")
            ? moment(new Date(created_at)).format("DD MMM Y ● hh:mm:ss")
            : moment(new Date(created_at + "Z")).format("DD MMM Y ● hh:mm:ss")}
        </div>
      ),
    },
    {
      key: "amount",
      title: "Amount [HVT]",
      dataIndex: "outputs",
      render: (outputs) => {
        const amount = outputs[0]?.amount ?? 0;
        return (
          <div className="grey">
            {Number(amount).toFixed(2)}
            <sub className="sub-script-text text-primary-color">HVT</sub>
          </div>
        );
      },
    },

    {
      key: "action",
      title: "Action",
      dataIndex: "outputs",
      render: (outputs) => {
        const address = outputs[0].to_address;
        const isWalletAddress = address === walletAddress ? true : false;
        let color = isWalletAddress ? "#87d068" : "#f50";
        let actionType = isWalletAddress ? "Recived" : "Sent";
        return (
          <div className="grey">
            <Tag
              style={{
                backgroundColor: "transparent",
                border: `1px solid ${color}`,
                color: color,
              }}
            >
              {actionType}
            </Tag>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <Table
        loading={loader}
        sticky
        scroll={{ x: screens.md ? true : 1000, y: 525 }}
        columns={columns}
        pagination={false}
        dataSource={walletTransaction}
        rowKey={"hash_key"}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px",
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
        }}
      >
        <div style={{ fontWeight: "bold", color: "#353535" }}>
          Total {transactionCount} items
        </div>
        <div>
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 100, border: `1px solid #353535` }}
          >
            <Select.Option value={5}>5 Rows</Select.Option>
            <Select.Option value={10}>10 Rows</Select.Option>
            <Select.Option value={20}>20 Rows</Select.Option>
            <Select.Option value={50}>50 Rows</Select.Option>
          </Select>{" "}
          <Button
            onClick={handlePrev}
            disabled={currentPage === 1}
            style={{ marginRight: 8 }}
          >
            Previous
          </Button>
          <Button
            onClick={handleNext}
            disabled={currentPage * pageSize >= transactionCount}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};

export default WalletTransactions;
